import React, { ReactNode } from 'react';
import { Modal, ModalProps, ButtonProps } from 'antd';
import './SAModal.css';

/**
 * Props for Custom Modal
 */
export interface CustomModalProps extends Omit<ModalProps, 'title' | 'onOk' | 'onCancel'> {
  title?: ReactNode;
  open?: boolean;
  children?: ReactNode;
  onOk?: () => void | Promise<void>;
  onCancel?: () => void;
  okText?: string;
  cancelText?: string;
  okButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
  isLoading?: boolean;
  variant?: 'default' | 'primary' | 'danger' | 'success' | 'warning' | 'info';
}

/**
 * Basic Custom Modal Component
 */
export const SAModal: React.FC<CustomModalProps> = ({
  title,
  children,
  open,
  onOk,
  onCancel,
  okText = 'OK',
  cancelText = 'Cancel',
  okButtonProps,
  cancelButtonProps,
  isLoading = false,
  variant = 'default',
  ...props
}) => {
  const [loading, setLoading] = React.useState(false);
  
  const handleOk = async () => {
    setLoading(true);
    try {
      await onOk?.();
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onCancel?.();
  };

  const variantClass = `sa-modal--${variant}`;

  return (
    <Modal
      open={open}
      title={<span className="fs15 fw600">{title}</span>}
      maskClosable={false}
      onOk={handleOk}
      onCancel={handleCancel}
      okText={okText}
      cancelText={cancelText}
      confirmLoading={loading || isLoading}
      okButtonProps={{
        className: 'sa-modal__ok-btn',
        style: { minWidth: 80,marginLeft:"12px",borderRadius:"4px" },
        ...okButtonProps,
      }}
      cancelButtonProps={{
        className: 'sa-modal__cancel-btn',
        style: { minWidth: 80,borderRadius:"4px" },
        ...cancelButtonProps,
      }}
      wrapClassName={`sa-modal ${variantClass}`}
      {...props}
    >
      <div className="sa-modal__content" style={{
        margin:"8px 0 16px 0"
      }}>{children}</div>
    </Modal>
  );
};


/**
 * Form Modal - For forms with custom validation
 */
export interface FormModalProps extends Omit<CustomModalProps, 'onOk'> {
  onOk?: (formData: any) => void | Promise<void>;
  onFormChange?: (formData: any) => void;
}

export const SAFormModal: React.FC<FormModalProps> = ({
  title,
  children,
  onOk,
  onCancel,
  okText = 'Submit',
  cancelText = 'Cancel',
  ...props
}) => {
  const handleOk = async () => {
    await onOk?.({});
  };

  return (
    <SAModal
      title={title}
      onOk={handleOk}
      onCancel={onCancel}
      okText={okText}
      cancelText={cancelText}
      {...props}
    >
      <div className="sa-modal__form">{children}</div>
    </SAModal>
  );
};

export default SAModal;
